/**
 * Left-pane editor for the free-text original note.
 *
 * This is the only pane that supports speech-to-text; users type or dictate here
 * before (and after) asking AI to structure the content. Optional AI structure
 * action sits as a small icon next to the microphone.
 */

import React from 'react';
import { Input, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import SpeechToTextWrapper from '@/components/structured-note/SpeechToTextWrapper';

const { Text } = Typography;
const { TextArea } = Input;

interface OriginalNoteEditorProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  maxLength?: number;
  showCount?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Optional icon button(s) shown next to the microphone (e.g. Structure with AI). */
  extraActions?: React.ReactNode;
  /** When false, hides the "Original Note" label (useful in plain compose mode). */
  showLabel?: boolean;
  /** When true, stretches the textarea to match the height of a sibling pane. */
  fillHeight?: boolean;
}

/**
 * Renders the labeled original-note textarea with mic controls and optional AI action.
 */
export function OriginalNoteEditor({
  value,
  onChange,
  rows = 5,
  maxLength,
  showCount = true,
  placeholder,
  disabled,
  extraActions,
  showLabel = true,
  fillHeight = false,
}: OriginalNoteEditorProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');

  return (
    <div
      style={{
        width: '100%',
        ...(fillHeight
          ? {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }
          : {}),
      }}
    >
      {showLabel ? (
        <Text strong style={{ display: 'block', marginBottom: token.marginXS, flexShrink: 0 }}>
          {t('structuredNote.originalNote')}
        </Text>
      ) : null}
      <div
        style={
          fillHeight
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
            : undefined
        }
      >
        <SpeechToTextWrapper
          value={value}
          onChange={(event) => onChange(event.target.value)}
          enabled={!disabled}
          extraActions={extraActions}
          fillHeight={fillHeight}
        >
          <TextArea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            rows={fillHeight ? undefined : rows}
            maxLength={maxLength}
            showCount={showCount}
            placeholder={placeholder}
            disabled={disabled}
          />
        </SpeechToTextWrapper>
      </div>
    </div>
  );
}
